/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */

import fecha from 'fecha';
import every from 'lodash/every';
import isArray from 'lodash/isArray';
import isNil from 'lodash/isNil';
import isNull from 'lodash/isNull';
import isNumber from 'lodash/isNumber';
import isObjectLike from 'lodash/isObjectLike';
import isString from 'lodash/isString';

import { isISOTimeString } from '../../iso-time-string';
import { parseURL } from '../../url';
import CommunicatorNumber from '../number/communicator-number';
import { DaysOfWeek } from '../../day-of-week';

/**
 * @import { TimestampString } from '../../communicator'
 * @import { ContentAttrs } from '../../content/content'
 * @import { Fraction } from '../../fraction'
 * @import { ISOTimeString } from '../../iso-time-string'
 */

/**
 * @typedef {{ id?: string, type?: 'layout' | 'media' | 'playlist' | '' } | null | undefined} ReferenceJsonType
 */

/**
 * Utility komunikátoru pro Ki-Wi Server.
 */
export default class CommunicatorUtility {
  /**
   * Načte pravdivostní hodnotu.
   * @param {string | null | undefined} value Příznak ve formátu, který vrací server.
   * @returns {boolean | null} Příznak jako `boolean`.
   */
  static parseBoolean(value) {
    if (isNil(value)) {
      return null;
    }
    if (!isString(value)) {
      throw new TypeError(`Invalid boolean ${JSON.stringify(value)}.`);
    }
    return value === 'true';
  }

  /**
   * Naparsuje položku `daysOfWeek` (číslo ve stringu) z JSON, která představuje
   * bitové pole s dny v týdnu, kde indexy jsou podle konvencí Ki-Wi Serveru
   * (0 = pondělí, ..., 5 = sobota, 6 = neděle) na {@link DaysOfWeek} s JS Date
   * konvencí (0 = neděle, 1 = pondělí, ..., 6 = sobota).
   * @param {string} daysOfWeekJsonString
   * @returns {DaysOfWeek}
   */
  static parseDaysOfWeek(daysOfWeekJsonString) {
    const v = CommunicatorNumber.fromStringRequired(daysOfWeekJsonString);
    // eslint-disable-next-line no-bitwise
    return DaysOfWeek.from(((v << 1) | (v >>> 6)) & 127);
  }

  /**
   * Zpracuje hodnotu na Fraction (0.0 - 1.0) z čísla nebo řetězce (0 - 100).
   * @param {number | string | null | undefined} value
   * @returns {Fraction}
   */
  static parsePercentage(value) {
    const fraction = this.parsePercentageOrUndefined(value);
    if (isNil(fraction)) {
      throw new TypeError(`Missing volume ${JSON.stringify(value)}.`);
    }
    return fraction;
  }

  /**
   * Zpracuje hodnotu na Fraction (0.0 - 1.0) z čísla nebo řetězce (0 - 100).
   * @param {number | string | null | undefined} value
   * @returns {Fraction | undefined}
   */
  static parsePercentageOrUndefined(value) {
    if (isNil(value)) {
      return undefined;
    }
    let number;
    if (isString(value)) {
      number = CommunicatorNumber.fromStringRequired(value);
    } else {
      number = CommunicatorNumber.fromNumberRequired(value);
    }

    if (!Number.isFinite(number) || number < 0 || number > 100) {
      throw new RangeError(`Invalid fraction value ${JSON.stringify(value)}.`);
    }

    return number / 100;
  }

  // TODO Samostatné metody pro vstupy `number` a `string`.
  // - Zrušit po odstranění posledních závislostí.
  /**
   * Zpracuje číslo.
   * @deprecated Používat CommunicatorNumber dle vstupního typu.
   * @param {string | number | null | undefined} value Číslo ve formátu, který vrací server.
   * @returns {number | null} Konečné číslo.
   */
  static parseNumber(value) {
    if (isNil(value)) {
      return null;
    } else if (isNumber(value)) {
      return CommunicatorNumber.fromNumberOrNull(value);
    } else if (isString(value)) {
      return CommunicatorNumber.fromStringOrNull(value);
    }
    throw new TypeError(`Invalid value ${JSON.stringify(value)}.`);
  }

  /**
   * Zpracuje odkaz na jiný objekt.
   *
   * Bohužel se trochu nevhodně typicky používá (ne)polymorfně, tj. že pro jednu
   * referenci se zavolá třikrát pro různý key, aby se naparsovalo typicky
   * layoutId, mediumId, playlistId.
   * @param {ReferenceJsonType} json
   * Část odpovědi na obsah zařízení s jedním odkazem.
   *
   * Z Ki-Wi Serveru prakticky chodí kromě řádných hodnot taky tyto reprezentace nenastavené reference:
   * - `{"id":"","type":""}`: pro ContentTask, Layer
   * - u Layoutu je celá položka bgMedia nepovinná, takže de facto pro `bgMedia?.ref` přijde undefined
   * - `{}`: všude jinde
   * @param {'layout' | 'media' | 'playlist'} key Klíč relace, která se má načíst.
   * @returns {string | undefined} Identifikátor na který se odkazuje.
   */
  static parseReference(json, key) {
    if (
      isNil(json) ||
      !isObjectLike(json) ||
      json.type !== key ||
      !isString(json.id) ||
      // Prázdné ID nechceme akceptovat kvůli `{"id":"","type":""}`.
      json.id.length === 0
    ) {
      return undefined;
    }
    return json.id;
  }

  /**
   * Zpracuje odkaz na jiný objekt ve formě (ne)polymorfního ContentAttrs,
   * tj. médium, layout, playlist nebo nic.
   * @param {ReferenceJsonType} json Část odpovědi na obsah zařízení s jedním odkazem.
   * @returns {ContentAttrs}
   */
  static parseReferenceContentAttrs(json) {
    return {
      layoutId: this.parseReference(json, 'layout'),
      mediumId: this.parseReference(json, 'media'),
      playlistId: this.parseReference(json, 'playlist')
    };
  }

  /**
   * Načte řetězec.
   * @param {string | null | undefined} value Řetězec ve formátu, který vrací server.
   * @returns {string | null} Řetězec jako `string`.
   */
  static parseStringOrNull(value) {
    if (isNil(value)) {
      return null;
    }
    if (!isString(value)) {
      throw new TypeError(`Invalid string ${JSON.stringify(value)}.`);
    }
    return value;
  }

  /**
   * Načte řetězec nebo vyhodí chybu.
   * @param {string} value Řetězec ve formátu, který vrací server.
   * @returns {string} Řetězec jako `string`.
   */
  static parseStringRequired(value) {
    if (isNil(value)) {
      throw new TypeError('Missing string.');
    }
    if (!isString(value)) {
      throw new TypeError(`Invalid string ${JSON.stringify(value)}.`);
    }
    return value;
  }

  /**
   * Načte řetězec obsahující URL nebo vyhodí chybu.
   * @param {string} value Řetězec ve formátu, který vrací server.
   * @returns {string} Řetězec s validní URL jako `string`.
   */
  static parseStringUrl(value) {
    const url = this.parseStringRequired(value);
    // Slouží jen pro validaci URL, není potřeba naparsovaná hodnota.
    parseURL(url);
    return url;
  }

  /**
   * Načte alfa kanál v procentech. Převede ho do rozsahu 0 - 1.
   * @param {number | null | undefined} backgroundColorAlphaPercentage Alfa kanál zadaný v rozsahu 0 - 100.
   * @returns {Fraction | undefined} Alfa kanál v rozsahu 0 - 1 nebo undefined.
   */
  static parseBackgroundColorAlphaPercentage(backgroundColorAlphaPercentage) {
    if (isNil(backgroundColorAlphaPercentage)) {
      return;
    }

    const bgColorAlphaPercentage = CommunicatorNumber.fromNumberRequired(
      backgroundColorAlphaPercentage
    );

    if (bgColorAlphaPercentage < 0 || bgColorAlphaPercentage > 100) {
      throw new TypeError(
        `Value backgroundColorAlphaPercentage ${JSON.stringify(
          backgroundColorAlphaPercentage
        )} is out of range.`
      );
    }

    return bgColorAlphaPercentage / 100;
  }

  // Ki-Wi Server vždy vrací `{ "endType": "timeLength", "timeLength": "$number" }`
  // (kromě main.module.media[0].playLength, kde je ale vždy napevno
  // `{ "endType": "indefinite" }`, což se ale v Player Lighter nikdy neparsuje).
  // Takže tím pádem se z hlediska typů nemá vracet null, ale při nevaliditě spíše
  // vyhodit výjimka.
  // TODO Samostatné metody pro vstupy `number` (jen u tracků v položce duration) a `string` (playLength).
  // - Přesunou do samostatného modulu a rozděli na 2 metody.
  // - Zvážit jestli nepovolit výstup NaN apod.
  /**
   * Zpracuje časový údaj nebo vyhodí chybu.
   * @param {string | number | null | undefined} value Čas ve formátu, který vrací server.
   * @returns {number} Počet ms.
   */
  static parseTime(value) {
    const number = CommunicatorUtility.parseNumber(value);
    if (isNull(number) || !Number.isFinite(number)) {
      throw new TypeError(`Invalid playLength ${JSON.stringify(value)}.`);
    }
    // TODO: Nemělo by se spíše odsekávat (a co pro případná záporná čísla - jsou vůbec povolena)?
    return Math.round(number * 1000);
  }

  // TODO Samostatné metody pro vstupy `number` a `string`.
  // - Přesunou do samostatného modulu a rozděli na 2 metody.
  // - Zvážit jestli nepovolit výstup NaN apod.
  /**
   * Zpracuje hlasitost nebo vyhodí chybu.
   * @param {number | string | null | undefined} value Hlasitost ve formátu, který vrací server.
   * @returns {Fraction} Hlasitost na škále od 0.0 (potichu) do 1.0 (nahlas).
   */
  static parseVolume(value) {
    const number = CommunicatorUtility.parseNumber(value);
    if (number === null || !Number.isFinite(number)) {
      throw new TypeError(`Invalid volume ${JSON.stringify(value)}.`);
    }
    return number / 100;
  }

  // TODO: #555 Přejmenovat na validate{Update,Content}Timestamp a vracet jen boolean a akceptovat jen string.
  // Případně vracet původní string při úspěchu či null při neúspěchu.
  /**
   * Zpracuje update/content timestamp z Ki-Wi Serveru a pro validní
   * ISO 8601 timestamp vrátí vstupní string jinak null.
   *
   * Nevalidní timestampy jsou i např. formátem validní, ale určující nevalidní timestamp,
   * např. 29.2. nepřestupného roku, 32. den měsíce, 99 minuta, atd.
   *
   * Pozor, tato funkce není vhodná pro obecné použití, jelikož nevrací naparsovaný Date objekt!
   * @param {string | null | undefined} value Datum ve formátu, který vrací server.
   * @returns {TimestampString | null} Původní hodnota nebo null, pokud to nebyl validní ISO 8601 datum.
   */
  static validateTimestampStringOrNull(value) {
    if (!isString(value)) {
      return null;
    }
    // TODO: Pozor, fecha ignoruje případné ms ve formátu a do naparsovaného
    // Date je nedá! A např. na rozdíl od new Date "parseru" vyžaduje
    // i explicitní minuty. A kdo ví, jak se chová pro různé TZ.
    const parsed = fecha.parse(value, 'isoDateTime');
    if (parsed === null || isNaN(parsed.valueOf())) {
      return null;
    }
    return value;
  }

  /**
   * Validujepole časů pro všechny dny v týdnu.
   * @param {[string, string, string, string, string, string, string]} json
   * @returns {[ISOTimeString, ISOTimeString, ISOTimeString, ISOTimeString, ISOTimeString, ISOTimeString, ISOTimeString]}
   * @throws {TypeError} Pokud validace selže.
   */
  static validateWeekTimesArray(json) {
    if (!isArray(json) || json.length !== 7 || !every(json, isISOTimeString)) {
      throw new TypeError(`Invalid task time array: ${JSON.stringify(json)}.`);
    }
    return /** @type {ReturnType<CommunicatorUtility.validateWeekTimesArray>} */ (
      json
    );
  }
}
